import { z } from "zod";
import { AnnotationDocumentSchema } from "./annotation";

export const TagSchema = z.object({
  id: z.string().uuid(),
  jobId: z.string().uuid(),
  name: z.string(),
  createdAt: z.string().datetime(),
});
export type Tag = z.infer<typeof TagSchema>;

export const CreateTagSchema = z.object({
  jobId: z.string().uuid(),
  name: z.string().trim().min(1).max(50),
});
export type CreateTagInput = z.infer<typeof CreateTagSchema>;

export const SetPhotoTagsSchema = z.object({
  tagIds: z.array(z.string().uuid()).max(20),
});
export type SetPhotoTagsInput = z.infer<typeof SetPhotoTagsSchema>;

export const PhotoUploaderSchema = z.object({
  id: z.string().uuid(),
  name: z.string().nullable(),
  email: z.string().email(),
  avatarUrl: z.string().url().nullable(),
});

export type PhotoUploader = z.infer<typeof PhotoUploaderSchema>;

export const MediaTypeSchema = z.enum(["photo", "video"]);
export type MediaType = z.infer<typeof MediaTypeSchema>;

/** Accepts both still images and videos for the unified media pipeline. */
const UploadContentTypeSchema = z
  .string()
  .refine((v) => v.startsWith("image/") || v.startsWith("video/"), {
    message: "contentType must be an image/* or video/* MIME type",
  });

export const PhotoSchema = z.object({
  id: z.string().uuid(),
  jobId: z.string().uuid(),
  userId: z.string().nullable(),
  storageKey: z.string(),
  thumbnailKey: z.string().nullable(),
  mediaType: MediaTypeSchema.default("photo"),
  durationMs: z.number().int().nonnegative().nullable().optional(),
  takenAt: z.string().datetime().nullable(),
  exifLat: z.number().nullable(),
  exifLng: z.number().nullable(),
  width: z.number().int().nullable(),
  height: z.number().int().nullable(),
  url: z.string().url().optional(),
  thumbnailUrl: z.string().url().optional(),
  /** Presigned read URL for the flattened annotated render (display surfaces prefer this). */
  annotatedUrl: z.string().url().nullable().optional(),
  hasAnnotations: z.boolean().optional(),
  uploadedBy: PhotoUploaderSchema.optional(),
  tags: z.array(TagSchema).optional().default([]),
  createdAt: z.string().datetime(),
});

export type Photo = z.infer<typeof PhotoSchema>;

export const RequestUploadUrlSchema = z.object({
  filename: z.string(),
  contentType: UploadContentTypeSchema,
  captureJobId: z.string().uuid().optional(),
  /** When true, do not pin to the geofence-matched job (user chose "New site" while inside a site). */
  skipGeofenceMatch: z.boolean().optional(),
  lat: z.number().min(-90).max(90).optional(),
  lng: z.number().min(-180).max(180).optional(),
  altitude: z.number().optional(),
  altitudeAccuracy: z.number().positive().optional(),
  takenAt: z.string().datetime().optional(),
  width: z.number().int().positive().optional(),
  height: z.number().int().positive().optional(),
  /** When true, the API issues a second presigned URL for the flattened annotated render. */
  hasAnnotations: z.boolean().optional(),
});

export type RequestUploadUrlInput = z.infer<typeof RequestUploadUrlSchema>;

export const UploadUrlResponseSchema = z.object({
  uploadUrl: z.string().url(),
  storageKey: z.string(),
  /** For video uploads: presigned PUT for the poster image, plus its key (null for photos). */
  thumbnailUploadUrl: z.string().url().nullable(),
  thumbnailKey: z.string().nullable(),
  /** For annotated captures: presigned PUT for the flattened render, plus its key (else null). */
  annotationUploadUrl: z.string().url().nullable(),
  annotatedStorageKey: z.string().nullable(),
  jobId: z.string().uuid().nullable(),
  needsJobAssignment: z.boolean(),
  matchedJob: z
    .object({
      id: z.string(),
      name: z.string(),
      address: z.string().nullable(),
    })
    .nullable(),
  nearbyJobs: z.array(
    z.object({
      id: z.string(),
      name: z.string(),
      address: z.string().nullable(),
      horizontalDistanceM: z.number(),
      verticalDistanceM: z.number().nullable(),
      isVerticalMatch: z.boolean(),
    }),
  ),
  suggestedJobName: z.string().nullable(),
});

export type UploadUrlResponse = z.infer<typeof UploadUrlResponseSchema>;

export const ConfirmUploadSchema = z.object({
  storageKey: z.string(),
  jobId: z.string().uuid(),
  mediaType: MediaTypeSchema.optional(),
  thumbnailKey: z.string().optional(),
  durationMs: z.number().int().nonnegative().optional(),
  takenAt: z.string().datetime().optional(),
  exifLat: z.number().optional(),
  exifLng: z.number().optional(),
  width: z.number().int().positive().optional(),
  height: z.number().int().positive().optional(),
  annotations: AnnotationDocumentSchema.optional(),
  annotatedStorageKey: z.string().optional(),
});

export type ConfirmUploadInput = z.infer<typeof ConfirmUploadSchema>;

const BULK_PHOTO_MAX = 40;

export const BulkConfirmItemSchema = z.object({
  storageKey: z.string(),
  takenAt: z.string().datetime().optional(),
  exifLat: z.number().optional(),
  exifLng: z.number().optional(),
  width: z.number().int().positive().optional(),
  height: z.number().int().positive().optional(),
});

export const BulkConfirmUploadSchema = z.object({
  jobId: z.string().uuid(),
  items: z.array(BulkConfirmItemSchema).min(1).max(BULK_PHOTO_MAX),
});

export type BulkConfirmUploadInput = z.infer<typeof BulkConfirmUploadSchema>;

export const BulkDeletePhotosSchema = z.object({
  jobId: z.string().uuid(),
  photoIds: z.array(z.string().uuid()).min(1).max(BULK_PHOTO_MAX),
});

export type BulkDeletePhotosInput = z.infer<typeof BulkDeletePhotosSchema>;

export const BulkMovePhotosSchema = z.object({
  sourceJobId: z.string().uuid(),
  targetJobId: z.string().uuid(),
  photoIds: z.array(z.string().uuid()).min(1).max(BULK_PHOTO_MAX),
});

export type BulkMovePhotosInput = z.infer<typeof BulkMovePhotosSchema>;

export const MovePhotoSchema = z.object({
  targetJobId: z.string().uuid(),
});

export type MovePhotoInput = z.infer<typeof MovePhotoSchema>;
